import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FileAccessGrantAdminListResponse, FileAccessGrantListFilters } from 'services/file-access-grant.service';
import { formatTaxNumber } from 'utils/formatters';
import GrantAccessTableFilters from './GrantAccessTableFilters';

type Props = {
  data: FileAccessGrantAdminListResponse;
  filters: FileAccessGrantListFilters;
  loading: boolean;
  exportLoading: boolean;
  onPageChange: (page: number) => void;
  onFilterChange: (filters: FileAccessGrantListFilters) => void;
  onDelete: (id: number) => void;
  onExport: () => void;
};

type Column = {
  label: string;
  field: keyof FileAccessGrantListFilters;
};

const columns: Column[] = [
  {
    label: 'Nome do usuário',
    field: 'personName',
  },
  {
    label: 'E-mail do usuário',
    field: 'personEmail',
  },
  {
    label: 'Nome da empresa',
    field: 'carrierBranchTradeName',
  },
  {
    label: 'CNPJ da empresa',
    field: 'carrierBranchDocumentNumber',
  },
];

const GrantAccessTable: React.FC<Props> = ({ data, filters, loading, exportLoading, onPageChange, onFilterChange, onDelete, onExport }) => {
  const totalPages = Math.ceil(data.total / data.limit);

  return (
    <Box>
      <GrantAccessTableFilters filters={filters} loading={loading} onFilterChange={onFilterChange} />

      <Paper elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.field} sx={{ pb: 0 }}>
                    <Typography fontWeight={600}>{col.label}</Typography>
                  </TableCell>
                ))}

                <TableCell width={60} />
              </TableRow>
            </TableHead>

            <TableBody>
              {data.items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ py: 1 }}>{row.personDetails.name}</TableCell>
                  <TableCell sx={{ py: 1 }}>{row.personDetails.email}</TableCell>
                  <TableCell sx={{ py: 1 }}>{row.carrierBranchDetails.companyName}</TableCell>
                  <TableCell sx={{ py: 1 }}>{formatTaxNumber(row.carrierBranchDetails.documentNumber)}</TableCell>

                  <TableCell sx={{ py: 1 }} align="right">
                    <IconButton onClick={() => onDelete(row.id)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum resultado encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" alignItems="end" mt={3}>
          <Box flex={1} />

          <Pagination count={totalPages} page={data.page} onChange={(_, page) => onPageChange(page)} shape="rounded" />

          <Box flex={1} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={onExport}
              disabled={exportLoading}
              startIcon={exportLoading ? <CircularProgress size={20} /> : undefined}
            >
              Exportar para Excel
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default GrantAccessTable;
